import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Warning, Plus, Trash, Info } from "@phosphor-icons/react";
import David from "@/components/David";
import FmDashboard from "@/components/fm/FmDashboard";
import UnitEconomics from "@/components/fm/UnitEconomics";
import Scenarios from "@/components/fm/Scenarios";
import Dscr from "@/components/fm/Dscr";
import CrsCheck from "@/components/fm/CrsCheck";
import Tracking from "@/components/fm/Tracking";
import ExportMenu from "@/components/fm/ExportMenu";
import GuidedChecklist from "@/components/fm/GuidedChecklist";
import ImportReview from "@/components/fm/ImportReview";
import { normalizeModelCtx, fyLabels } from "@/lib/fmInterview";

const C = { bg: "#050E0D", panel: "#0A2826", teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B" };
const YEARS = ["f1", "f2", "f3", "f4", "f5"];
const fmt = (n) => (n === 0 ? "–" : n < 0 ? `(${Math.abs(n).toLocaleString("en-KE", { maximumFractionDigits: 0 })})` : n.toLocaleString("en-KE", { maximumFractionDigits: 0 }));

/* Spec cell language adapted to the dark theme:
   input cells = light-blue tint (founder types here) · computed = plain */
const inputCls = "w-full rounded px-2.5 py-2 text-sm font-semibold focus:outline-none";
const inputStyle = { backgroundColor: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.35)", color: "#93C5FD" };

const NumIn = ({ value, onChange, testid, placeholder }) => (
  <div>
    <input type="number" data-testid={testid} className={inputCls} style={inputStyle}
      value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))} />
    {Math.abs(value ?? 0) >= 10000 && (
      <div className="text-[10px] mt-0.5" style={{ color: "rgba(147,197,253,0.55)" }}>≈ {Math.abs(value).toLocaleString("en-KE")}</div>
    )}
  </div>
);

/* Percent input — founder types "3" for 3%; stored as decimal (0.03) so the
   engine stays unchanged. Rounding kills float drift (0.03*100 = 3.0000…4). */
const toPct = (v) => (v == null ? "" : Math.round(v * 10000) / 100);
const PctIn = ({ value, onChange, testid, placeholder }) => (
  <div className="relative">
    <input type="number" step="0.5" data-testid={testid} className={inputCls + " pr-7"} style={inputStyle}
      value={toPct(value)} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value) / 100)} />
    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-bold pointer-events-none" style={{ color: "rgba(147,197,253,0.6)" }}>%</span>
  </div>
);

const Lbl = ({ children, hint, info }) => (
  <div className="mb-1">
    <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/50 inline-flex items-center gap-1.5">
      {children}
      {info && (
        <span className="relative group inline-flex cursor-help" data-testid="fm-info-icon">
          <Info size={13} style={{ color: "rgba(123,211,206,0.55)" }} />
          <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-60 rounded-md px-3 py-2.5 text-[11px] leading-relaxed normal-case tracking-normal font-medium z-50"
            style={{ backgroundColor: "#0D3330", border: "1px solid rgba(123,211,206,0.35)", color: "#D8EEEC", boxShadow: "0 10px 28px rgba(0,0,0,0.55)" }}>
            {info}
          </span>
        </span>
      )}
    </span>
    {hint && <div className="text-[10px] text-white/30">{hint}</div>}
  </div>
);

export default function FinModelBuilder() {
  const { fmId } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [meta, setMeta] = useState(null);
  const [out, setOut] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [saving, setSaving] = useState(false);
  const saveT = useRef(null);
  const pending = useRef({});

  const save = (patch) => {
    // Accumulate patches so rapid edits (<700ms apart) all reach the server
    const p = pending.current;
    if (patch.assumptions) {
      p.assumptions = p.assumptions || {};
      for (const [k, v] of Object.entries(patch.assumptions)) {
        p.assumptions[k] = (v && typeof v === "object" && !Array.isArray(v) &&
          p.assumptions[k] && typeof p.assumptions[k] === "object" && !Array.isArray(p.assumptions[k]))
          ? { ...p.assumptions[k], ...v } : v;
      }
    }
    if (patch.milestones) p.milestones = patch.milestones;
    if (patch.profile) p.profile = { ...(p.profile || {}), ...patch.profile };
    if (patch.historicals) {
      p.historicals = p.historicals || {};
      for (const [k, v] of Object.entries(patch.historicals)) {
        p.historicals[k] = { ...(p.historicals[k] || {}), ...v };
      }
    }
    if (patch.actuals) {
      p.actuals = p.actuals || {};
      for (const [k, v] of Object.entries(patch.actuals)) {
        p.actuals[k] = { ...(p.actuals[k] || {}), ...v };
      }
    }

    setModel((m) => {
      const next = { ...m };
      if (patch.assumptions) next.assumptions = Object.fromEntries(
        Object.entries({ ...m.assumptions }).map(([k, v]) => [k, patch.assumptions[k] ? { ...v, ...patch.assumptions[k] } : v]));
      if (patch.milestones) next.milestones = patch.milestones;
      if (patch.historicals) {
        next.historicals = { ...(m.historicals || {}) };
        for (const [k, v] of Object.entries(patch.historicals)) {
          next.historicals[k] = { ...(next.historicals[k] || {}), ...v };
        }
      }
      if (patch.actuals) {
        next.actuals = { ...(m.actuals || {}) };
        for (const [k, v] of Object.entries(patch.actuals)) {
          next.actuals[k] = { ...(next.actuals[k] || {}), ...v };
        }
      }
      return next;
    });
    clearTimeout(saveT.current);
    saveT.current = setTimeout(async () => {
      const body = pending.current;
      pending.current = {};
      setSaving(true);
      try {
        const { data } = await api.put(`/fm/models/${fmId}`, body);
        setModel(normalizeModelCtx(data));
      } catch { toast.error("Save failed"); }
      setSaving(false);
    }, 700);
  };

  useEffect(() => {
    api.get("/fm/meta").then((r) => setMeta(r.data));
    api.get(`/fm/models/${fmId}`).then((r) => setModel(normalizeModelCtx(r.data))).catch(() => navigate("/dashboard"));
  }, [fmId, navigate]);

  const recompute = () => api.get(`/fm/models/${fmId}/compute`).then((r) => setOut(r.data)).catch(() => {});
  useEffect(() => { if (model) recompute(); /* eslint-disable-next-line */ }, [model?.updated_at]);

  const preRevenue = model?.profile?.revenue_status === "pre_revenue";
  const variant = model?.profile?.revenue_model;
  const vmeta = meta?.revenue_models?.[variant];
  const rev = model?.assumptions?.revenue || {};
  const costs = model?.assumptions?.costs || {};
  const capital = model?.assumptions?.capital || {};
  const setRev = (k, v) => save({ assumptions: { revenue: { ...rev, [k]: v } } });
  const setCost = (k, v) => save({ assumptions: { costs: { ...costs, [k]: v } } });
  const setCap = (k, v) => save({ assumptions: { capital: { ...capital, [k]: v } } });

  const tabs = useMemo(() => [
    ["dashboard", "Dashboard"],
    ...(preRevenue ? [] : [["historicals", "Historicals"]]),
    ["tracking", "Tracking"],
    ["revenue", "Revenue (monthly)"],
    ["pnl", "P&L (monthly)"],
    ["cashflow", "Cashflow (monthly)"],
    ["balance", "Balance sheet"],
    ["unitecon", "Unit economics", "Profit per unit sold — does each sale actually make money?"],
    ["scenarios", "Scenarios"],
    ["dscr", "Debt / DSCR", "Debt Service Coverage Ratio — can the business afford its loan repayments?"],
    ["crs", "CRS check", "Capital Readiness Score — a funder-style checklist of what your model still needs"],
  ], [preRevenue]);

  if (!model || !meta) return <div className="min-h-screen" style={{ backgroundColor: C.bg }} />;

  const modelEmpty = out ? !(out.dashboard?.f1_revenue || out.dashboard?.f5_revenue) : true;
  const volShape = rev.volume?.shape || "flat";

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: C.bg }} data-testid="fm-builder">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 z-20" style={{ backgroundColor: C.bg }}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="text-white/60 hover:text-white" data-testid="fm-back"><ArrowLeft size={18} /></button>
          <div>
            <div className="text-white font-extrabold">{model.name}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{vmeta?.label} · {preRevenue ? "Pre-revenue" : "Post-revenue"} · {out?.calendar?.year_labels ? `${out.calendar.year_labels[0]}–${out.calendar.year_labels[4]}` : "F1–F5"} monthly</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-[11px] text-white/40">{saving ? "Saving…" : "Saved"} {out && <span className="ml-3" style={{ color: C.tealSoft }}>{out.assumption_count} assumptions · export {out.export_price_credits} cr</span>}</div>
          <button onClick={() => navigate(`/fm/${fmId}/interview`)} data-testid="fm-edit-answers"
            className="text-xs font-bold px-3.5 py-2 rounded-md"
            style={{ backgroundColor: "rgba(10,139,133,0.16)", color: C.tealSoft, border: "1px solid rgba(10,139,133,0.4)" }}
            title="Walk back through David's questions and change any answer — the model updates live.">
            Edit answers
          </button>
          <ExportMenu fmId={fmId} modelName={model.name} />
        </div>
      </header>

      {/* annual summary strip — hidden until the model has numbers so a fresh
          model isn't greeted with dashes and scary negative EBITDA */}
      {out && !modelEmpty && (
        <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-[1300px] mx-auto" data-testid="fm-summary-strip">
          {YEARS.map((y, yi) => (
            <div key={y} className="rounded-lg px-3 py-2.5" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.12)" }}>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{out.calendar?.year_labels?.[yi] || y.toUpperCase()} revenue</div>
              <div className="text-sm font-extrabold text-white mt-0.5">{fmt(out.annual.revenue[y].revenue)}</div>
              <div className="text-[10px]" style={{ color: out.annual.pnl[y].ebitda >= 0 ? C.tealSoft : C.orange }}>EBITDA {fmt(out.annual.pnl[y].ebitda)}</div>
            </div>
          ))}
        </div>
      )}
      {out?.warnings?.length > 0 && !modelEmpty && (
        <div className="max-w-[1300px] mx-auto px-6">
          {out.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-sm rounded-md px-4 py-3 mb-2" data-testid={`fm-warning-${i}`}
              style={{ backgroundColor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.4)", color: "#FCD9A0" }}>
              <Warning size={16} style={{ color: C.orange, marginTop: 2 }} weight="fill" /> {w}
            </div>
          ))}
        </div>
      )}

      <div className="max-w-[1300px] mx-auto px-6">
        <div className="flex gap-1 border-b border-white/10 mb-6 flex-wrap">
          {tabs.map(([id, label, tip]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`fm-tab-${id}`}
              title={tip || undefined}
              className="px-4 py-2.5 text-sm font-semibold transition-colors"
              style={{ color: tab === id ? C.orange : "rgba(255,255,255,0.45)", borderBottom: tab === id ? `2px solid ${C.orange}` : "2px solid transparent" }}>
              {label}
            </button>
          ))}
        </div>

        {tab === "assumptions" && (
          <div data-testid="fm-assumptions">
            {(model.seeded_defaults || []).length > 0 && (
              <div className="text-[11px] rounded px-3 py-2 mb-4" data-testid="fm-seeded-note"
                style={{ backgroundColor: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", color: "#FCD9A0" }}>
                Starter numbers pre-filled for a typical {model.profile?.sector || ""} {vmeta?.label} business ({model.seeded_defaults.join(", ")}) — adjust everything to your reality.
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* B.1 revenue drivers */}
            <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
              <div className="text-sm font-extrabold" style={{ color: C.tealSoft }}>B.1 · Revenue drivers <span className="text-white/30 font-normal">— {vmeta?.formula}</span></div>
              {!preRevenue && (model.profile?.current_mrr > 0 || model.profile?.current_annual_revenue > 0) && (
                <div className="text-[11px] rounded px-3 py-2" data-testid="fm-revenue-context"
                  style={{ backgroundColor: "rgba(10,139,133,0.12)", border: "1px solid rgba(10,139,133,0.35)", color: C.tealSoft }}>
                  You told us you're doing {model.profile.current_mrr > 0
                    ? <>≈ <b>KES {fmt(model.profile.current_mrr)} MRR</b></>
                    : <>≈ <b>KES {fmt(Math.round(model.profile.current_annual_revenue / 12))} / month</b></>} today — set your drivers so month 1 lands near that.
                </div>
              )}
              {variant !== "sku_retail" && (<>
              <div>
                <Lbl hint="How the volume grows within each year — layered on seasonality." info="Your growth shape: Flat = same volume every month, Ramp = climbs steadily from month 1 to month 60, % MoM = compounds every month like interest.">{vmeta?.volume_label}</Lbl>
                <div className="flex gap-2 mb-2">
                  {[["flat", "Flat"], ["ramp", "Ramp"], ["mom", "% MoM"]].map(([v, l]) => (
                    <button key={v} data-testid={`fm-shape-${v}`} onClick={() => setRev("volume", { ...rev.volume, shape: v })}
                      className="px-3 py-1.5 rounded text-xs font-bold"
                      style={{ backgroundColor: volShape === v ? C.teal : "rgba(255,255,255,0.06)", color: volShape === v ? "#fff" : "rgba(255,255,255,0.5)" }}>{l}</button>
                  ))}
                </div>
                {volShape === "flat" && <NumIn testid="fm-vol-value" value={rev.volume?.value} onChange={(v) => setRev("volume", { ...rev.volume, value: v })} placeholder="Monthly value" />}
                {volShape === "ramp" && (
                  <div className="grid grid-cols-2 gap-2">
                    <NumIn testid="fm-vol-start" value={rev.volume?.start} onChange={(v) => setRev("volume", { ...rev.volume, start: v })} placeholder="Month 1" />
                    <NumIn testid="fm-vol-end" value={rev.volume?.end} onChange={(v) => setRev("volume", { ...rev.volume, end: v })} placeholder="Month 60" />
                  </div>
                )}
                {volShape === "mom" && (
                  <div className="grid grid-cols-2 gap-2">
                    <NumIn testid="fm-vol-value" value={rev.volume?.value} onChange={(v) => setRev("volume", { ...rev.volume, value: v })} placeholder="Starting value" />
                    <PctIn testid="fm-vol-rate" value={rev.volume?.rate} onChange={(v) => setRev("volume", { ...rev.volume, rate: v })} placeholder="% MoM e.g. 8" />
                  </div>
                )}
              </div>
              <div><Lbl info="What you charge per unit / customer / transaction. It automatically steps up each year by your price increase %.">{vmeta?.price_label}</Lbl><NumIn testid="fm-price" value={rev.price} onChange={(v) => setRev("price", v)} /></div>
              </>)}
              <div className="grid grid-cols-2 gap-3">
                {variant === "saas" && (<>
                  <div><Lbl hint="% of customers lost per month" info="The share of paying customers who cancel each month. 3% churn means 3 in 100 customers leave monthly — lower churn = faster compounding growth.">Monthly churn</Lbl><PctIn testid="fm-churn" value={rev.churn_pct} onChange={(v) => setRev("churn_pct", v)} /></div>
                  <div><Lbl info="Extra revenue from existing customers upgrading or buying more, as a % of MRR. 5% means current customers spend 5% more on top of their subscriptions.">Expansion % of MRR</Lbl><PctIn value={rev.expansion_pct} onChange={(v) => setRev("expansion_pct", v)} /></div>
                  <div><Lbl info="Non-recurring income each month — setup fees, training, once-off projects. Added on top of subscription revenue.">One-off revenue / month</Lbl><NumIn value={rev.oneoff_monthly} onChange={(v) => setRev("oneoff_monthly", v)} /></div>
                  <div><Lbl info="How long an average customer stays before cancelling. Used for LTV (lifetime value) in your unit economics.">Customer life (months)</Lbl><NumIn value={rev.customer_life_months} onChange={(v) => setRev("customer_life_months", v)} /></div>
                </>)}
                {variant === "b2b_contract" && (<>
                  <div><Lbl info="How long each client contract runs before it comes up for renewal — 12 = annual contracts.">Contract length (months)</Lbl><NumIn value={rev.contract_length_months} onChange={(v) => setRev("contract_length_months", v)} /></div>
                  <div><Lbl hint="% of contracts that renew" info="Of contracts reaching the end of their term, what share sign again? 60% means 6 in 10 clients renew.">Renewal rate</Lbl><PctIn value={rev.renewal_pct} onChange={(v) => setRev("renewal_pct", v)} /></div>
                </>)}
                {variant === "transactional" && (<>
                  <div><Lbl info="How many transactions the average active user makes per month.">Transactions / user / month</Lbl><NumIn value={rev.txn_frequency} onChange={(v) => setRev("txn_frequency", v)} /></div>
                  <div><Lbl hint="Your cut of each transaction" info="Your commission on each transaction's value. A 10% take rate on KES 1,000 of volume = KES 100 revenue for you. Total volume is GMV; your revenue is GMV × take rate.">Take rate</Lbl><PctIn value={rev.take_rate_pct} onChange={(v) => setRev("take_rate_pct", v)} /></div>
                </>)}
                {variant === "unit_sales" && (
                  <div><Lbl info="What each unit costs you to buy or make — the gap between this and your selling price is your gross margin.">Cost per unit (KES)</Lbl><NumIn value={rev.unit_cost} onChange={(v) => setRev("unit_cost", v)} /></div>
                )}
                {variant === "project" && (<>
                  <div><Lbl hint="% of leads that convert" info="Of the leads you generate, what share become paying projects? 25% = 1 project won per 4 leads.">Lead conversion</Lbl><PctIn value={rev.conversion_pct} onChange={(v) => setRev("conversion_pct", v)} /></div>
                  <div><Lbl info="Extra projects from returning clients, as a % on top of new wins. 10% means every 10 new projects bring 1 more from repeat business.">Repeat-client uplift</Lbl><PctIn value={rev.repeat_pct} onChange={(v) => setRev("repeat_pct", v)} /></div>
                </>)}
                {variant === "manufacturing" && (<>
                  <div><Lbl hint="Max units the plant can produce" info="Your production ceiling per month at full utilisation — output can never exceed this.">Plant capacity (units/mo)</Lbl><NumIn testid="fm-capacity" value={rev.capacity_units} onChange={(v) => setRev("capacity_units", v)} /></div>
                  <div><Lbl hint="% of output scrapped" info="Production that fails quality checks or is wasted. 5% scrap means you must produce ~105 units to sell 100 — it raises your unit cost.">Scrap / yield loss</Lbl><PctIn testid="fm-scrap" value={rev.scrap_pct} onChange={(v) => setRev("scrap_pct", v)} /></div>
                  <div><Lbl info="Raw material cost to make one unit — the core of your COGS.">Material cost / unit (KES)</Lbl><NumIn testid="fm-material-cost" value={rev.material_cost_unit} onChange={(v) => setRev("material_cost_unit", v)} /></div>
                </>)}
                {variant === "agribusiness" && (<>
                  <div><Lbl hint="% lost after harvest" info="Produce lost between harvest and sale — spoilage, transport damage, rejects. 15% loss means only 85% of what you grow earns revenue.">Post-harvest loss</Lbl><PctIn testid="fm-loss" value={rev.loss_pct} onChange={(v) => setRev("loss_pct", v)} /></div>
                  <div><Lbl hint="Seed, fertiliser, labour per unit produced" info="Everything it costs to produce one unit before harvest.">Input cost / unit (KES)</Lbl><NumIn testid="fm-input-cost" value={rev.input_cost_unit} onChange={(v) => setRev("input_cost_unit", v)} /></div>
                </>)}
                <div><Lbl hint="Applied annually" info="How much you raise prices each year. 5% means every price steps up 5% at the start of each new forecast year.">Price increase %/yr</Lbl><PctIn value={rev.price_increase_pct} onChange={(v) => setRev("price_increase_pct", v)} /></div>
              </div>
              {variant === "mixed" && (
                <MixedStreams rev={rev} setRev={setRev} />
              )}
              {variant === "sku_retail" && (
                <SkuTable rev={rev} setRev={setRev} />
              )}
              <ExtraLines rev={rev} setRev={setRev} />
              {variant === "agribusiness" && (
                <Seasonality rev={rev} setRev={setRev} />
              )}
            </div>

            {/* B.2 costs + B.4 capital */}
            <div className="space-y-6">
              <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
                <div className="text-sm font-extrabold" style={{ color: C.tealSoft }}>B.2 · Cost drivers</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Lbl hint="% of revenue" info="Cost of Goods Sold — what it directly costs you to deliver each sale (materials, hosting, payment fees, delivery). Entered as a % of revenue; the rest is your gross profit.">COGS % of revenue</Lbl><PctIn testid="fm-cogs" value={costs.cogs_pct} onChange={(v) => setCost("cogs_pct", v)} /></div>
                  <div><Lbl info="Products returned, damaged or written off — added on top of COGS. E.g. 2 means an extra 2% of COGS is lost to waste or returns.">Wastage / returns %</Lbl><PctIn value={costs.wastage_pct} onChange={(v) => setCost("wastage_pct", v)} /></div>
                  <div className="col-span-2"><Lbl hint="Fixed KES (per year) or % of revenue" info="Sales & Marketing spend — ads, commissions, events, sales tools. Choose a fixed monthly KES amount set per year, or a % of revenue that scales as you grow.">S&M spend</Lbl>
                    <div className="flex gap-2 items-end">
                      <select className="rounded px-2 py-2 text-xs bg-white/10 text-white border border-white/10 shrink-0" value={costs.sm_mode || "fixed"} onChange={(e) => setCost("sm_mode", e.target.value)} data-testid="fm-sm-mode">
                        <option value="fixed">KES</option><option value="pct">%</option>
                      </select>
                      {(costs.sm_mode || "fixed") === "pct"
                        ? <PctIn testid="fm-sm" value={costs.sm_value} onChange={(v) => setCost("sm_value", v)} />
                        : (
                          <div className="grid grid-cols-5 gap-1.5 flex-1" data-testid="fm-sm-yearly">
                            {["f1", "f2", "f3", "f4", "f5"].map((yr, i) => (
                              <div key={yr}>
                                <div className="text-[9px] font-bold text-white/30 mb-0.5">Y{i + 1}</div>
                                <input type="number" className={inputCls} style={inputStyle} placeholder={i === 0 ? "KES/mo" : "= prev"}
                                  data-testid={`fm-sm-yearly-${yr}`}
                                  value={((costs.sm_yearly || {})[yr] ?? (i === 0 ? (costs.sm_value ?? null) : null)) ?? ""}
                                  onChange={(e) => setCost("sm_yearly", { ...(costs.sm_yearly || {}), [yr]: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                  <YearlyCost label="Tech & infra / month" testid="fm-tech-yearly" base="tech" scalarKey="tech_monthly" costs={costs} setCost={setCost}
                    info="Software, hosting, internet, equipment leases — your technology running costs. Set the monthly amount for each year so it can step up as the business grows." />
                  <YearlyCost label="Rent & facilities / month" testid="fm-rent-yearly" base="rent" scalarKey="rent_monthly" costs={costs} setCost={setCost}
                    info="Office / warehouse rent, utilities, security. Set it per year — e.g. KES 50,000/mo in Year 1, but more from Year 2 when the team grows and you need bigger space." />
                  <YearlyCost label="Other G&A / month" testid="fm-other-yearly" base="other" scalarKey="other_monthly" costs={costs} setCost={setCost}
                    info="General & Administrative — accounting, legal, insurance, licences, bank charges and everything else that keeps the lights on." />
                  <div><Lbl hint="Kenya default 6%" info="How much your costs rise each year. Applied automatically ONLY to costs you haven't set per-year above — per-year values are used exactly as you typed them.">Cost inflation %/yr</Lbl><PctIn value={costs.inflation_pct} onChange={(v) => setCost("inflation_pct", v)} /></div>
                  <div><Lbl hint="For normalised EBITDA" info="What you'd have to pay someone else to do your job. Funders use it to 'normalise' profit — if you pay yourself below market rate, real profitability is lower than it looks.">Market-rate owner salary</Lbl><NumIn testid="fm-market-salary" value={costs.market_rate_owner_salary} onChange={(v) => setCost("market_rate_owner_salary", v)} /></div>
                </div>
                <Headcount costs={costs} setCost={setCost} yl={fyLabels(model)} />
              </div>
              <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
                <div className="text-sm font-extrabold" style={{ color: C.tealSoft }}>B.4 · Capital & CapEx</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Lbl info="Cash in the bank on day 1 of the forecast — where your runway starts counting from.">Opening cash (KES)</Lbl><NumIn value={capital.opening_cash} onChange={(v) => setCap("opening_cash", v)} /></div>
                  <div><Lbl hint="Kenya standard 30%" info="Corporate tax charged on profit before tax. Losses carry forward — you only start paying once cumulative losses are used up.">Income tax rate</Lbl><PctIn value={model.assumptions.tax?.income_tax_rate} onChange={(v) => save({ assumptions: { tax: { ...model.assumptions.tax, income_tax_rate: v } } })} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Lbl hint="Days customers take to pay" info="Days Sales Outstanding — how long customers take to pay after invoicing. Higher DSO = more cash stuck in unpaid invoices.">DSO (days)</Lbl><NumIn value={capital.dso_days} onChange={(v) => setCap("dso_days", v)} /></div>
                  <div><Lbl hint="Days you take to pay suppliers" info="Days Payables Outstanding — how long you take to pay suppliers. Higher DPO keeps cash in your account longer.">DPO (days)</Lbl><NumIn value={capital.dpo_days} onChange={(v) => setCap("dpo_days", v)} /></div>
                  <div><Lbl info="How many days of stock you hold. 45 days means a month and a half of sales sits on the shelf — cash tied up in inventory.">Inventory (days)</Lbl><NumIn value={capital.inventory_days} onChange={(v) => setCap("inventory_days", v)} /></div>
                </div>
                <ItemsTable label="Equity raises" items={capital.equity_raises || []} testid="fm-equity"
                  info="Money raised by selling shares — no repayment, but you give up ownership. Month # is when the cash lands in your account."
                  cols={[["label", "Round (e.g. Seed)", "text"], ["amount", "Amount", "num"], ["month", "Month #", "num"]]}
                  onChange={(items) => setCap("equity_raises", items)} />
                <ItemsTable label="Debt facilities" items={capital.debt_facilities || []} testid="fm-debt"
                  info="Loans — repaid monthly with interest. Rate is the annual interest %, term is the repayment period, start month is when you draw the money down."
                  cols={[["name", "Lender", "text"], ["principal", "Principal", "num"], ["interest_rate", "Rate % e.g. 16", "pct"], ["term_months", "Term (mo)", "num"], ["start_month", "Start mo", "num"]]}
                  onChange={(items) => setCap("debt_facilities", items)} />
                <ItemsTable label="Capital expenditure" items={capital.capex || []} testid="fm-capex"
                  info="Big one-off purchases (equipment, vehicles, fit-out). Cash leaves in the purchase month, but the cost hits profit gradually as depreciation over its useful life."
                  cols={[["item", "Item", "text"], ["cost", "Cost", "num"], ["month", "Month #", "num"], ["life_years", "Life (yrs)", "num"]]}
                  onChange={(items) => setCap("capex", items)} />
              </div>
            </div>
          </div>
          </div>
        )}

        {tab === "milestones" && (
          <Milestones model={model} save={save} />
        )}

        {tab === "revenue" && out && <MonthlyTable data={out.revenue.months} cal={out.calendar} title="Section D · Monthly revenue — formula-derived from your assumptions" testid="fm-revenue-table" />}
        {tab === "pnl" && out && <MonthlyTable data={out.pnl.months} cal={out.calendar}
          fields={["revenue", "cogs", "gross_profit", "salaries", "sales_marketing", "tech", "rent", "other", "ebitda", "ebitda_normalised", "depreciation", "ebit", "interest", "tax", "net_profit", "cumulative_net"]}
          title="Section E · Monthly P&L — locked formula cells (CRS Check 3)" testid="fm-pnl-table" />}
        {tab === "cashflow" && out && <MonthlyTable data={out.cashflow.months} cal={out.calendar}
          fields={["opening_cash", "net_profit", "depreciation", "delta_receivables", "delta_inventory", "delta_payables", "operating", "capex", "debt_drawdown", "debt_repayment", "equity_raised", "financing", "net_cashflow", "closing_cash"]}
          title="Section F · Monthly cashflow — closing cash chains into next month's opening (hard link)" testid="fm-cashflow-table" />}
        {tab === "balance" && out && <BalanceSheet bs={out.balance_sheet} yl={out.calendar?.year_labels} />}
        {tab === "historicals" && (
          <div>
            <ImportUpload fmId={fmId} onDone={(m) => { setModel(normalizeModelCtx(m)); recompute(); }}
              label="Import financials — upload your Excel model or management accounts (PDF)" />
            <Historicals model={model} save={save} fmId={fmId} cal={out?.calendar} />
          </div>
        )}
        {model.import_session?.status === "needs_review" && (
          <ImportReview model={model}
            onApplied={(m) => { setModel(normalizeModelCtx(m)); recompute(); }}
            onDismiss={() => setModel({ ...model, import_session: { ...model.import_session, status: "dismissed" } })} />
        )}

        {tab === "dashboard" && out && (
          <div className="space-y-6">
            <GuidedChecklist model={model} out={out} setTab={setTab} />
            <FmDashboard out={out} setTab={setTab} model={model} />
          </div>
        )}
        {tab === "tracking" && out && <Tracking out={out} model={model} save={save} />}
        {tab === "unitecon" && out && <UnitEconomics out={out} />}
        {tab === "scenarios" && out && <Scenarios out={out} model={model} save={save} />}
        {tab === "dscr" && out && <Dscr out={out} />}
        {tab === "crs" && out && <CrsCheck out={out} setTab={setTab} preRevenue={preRevenue} model={model} fmId={fmId} />}
      </div>
      <David fmId={fmId} varianceAlert={out?.tracking?.summary?.ytd_pct_of_forecast != null && out.tracking.summary.ytd_pct_of_forecast < 0.8 ? out.tracking.summary : null} />
    </div>
  );
}

function ExtraLines({ rev, setRev }) {
  const lines = rev.extra_lines || [];
  const upd = (i, k, v) => setRev("extra_lines", lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div data-testid="fm-extra-lines">
      <Lbl info="Most businesses earn from more than one place. Add up to 4 extra revenue lines on top of your main driver (5 total) — e.g. setup fees, advertising income, commissions, a second product. Each line starts at a monthly amount and grows at its own rate; all lines are added to total revenue.">
        Additional revenue lines
      </Lbl>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-2 items-center">
          <input className={inputCls} style={inputStyle} placeholder="Name (e.g. Setup fees)" value={l.name || ""}
            onChange={(e) => upd(i, "name", e.target.value)} data-testid={`fm-line-${i}-name`} />
          <NumIn testid={`fm-line-${i}-monthly`} value={l.monthly} onChange={(v) => upd(i, "monthly", v)} placeholder="KES / month" />
          <PctIn testid={`fm-line-${i}-growth`} value={l.growth_mom} onChange={(v) => upd(i, "growth_mom", v)} placeholder="Growth %/mo" />
          <button onClick={() => setRev("extra_lines", lines.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400" data-testid={`fm-line-${i}-remove`}><Trash size={15} /></button>
        </div>
      ))}
      {lines.length < 4 && (
        <button onClick={() => setRev("extra_lines", [...lines, { name: "", monthly: null, growth_mom: null }])}
          className="text-xs font-bold inline-flex items-center gap-1" style={{ color: C.tealSoft }} data-testid="fm-add-line">
          <Plus size={12} weight="bold" /> Add revenue line ({lines.length + 1}/5 used)
        </button>
      )}
    </div>
  );
}

function YearlyCost({ label, info, base, scalarKey, costs, setCost, testid }) {
  const yearlyKey = `${base}_yearly`;
  const yearly = costs[yearlyKey] || {};
  const scalar = costs[scalarKey];
  const yrs = ["f1", "f2", "f3", "f4", "f5"];
  const shown = (yr, i) => (yearly[yr] !== undefined && yearly[yr] !== null) ? yearly[yr] : (i === 0 ? (scalar ?? null) : null);
  return (
    <div className="col-span-2" data-testid={testid}>
      <Lbl info={info} hint="Monthly amount for each year — later years reuse the last value you set. Cost inflation is NOT added on top of values you enter here.">{label}</Lbl>
      <div className="grid grid-cols-5 gap-1.5">
        {yrs.map((yr, i) => (
          <div key={yr}>
            <div className="text-[9px] font-bold text-white/30 mb-0.5">Y{i + 1}</div>
            <input type="number" className={inputCls} style={inputStyle} placeholder={i === 0 ? "e.g. 50000" : "= prev"}
              data-testid={`${testid}-${yr}`}
              value={shown(yr, i) ?? ""}
              onChange={(e) => setCost(yearlyKey, { ...yearly, [yr]: e.target.value === "" ? null : parseFloat(e.target.value) })} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportUpload({ fmId, onDone, label }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className="block rounded-lg px-4 py-3 mb-5 cursor-pointer transition-colors hover:bg-white/5"
      style={{ border: "1.5px dashed rgba(245,158,11,0.45)", backgroundColor: "rgba(245,158,11,0.04)" }}
      data-testid="fm-import-upload">
      <input type="file" className="hidden" multiple accept=".xlsx,.xlsm,.pdf,.csv" disabled={busy}
        onChange={async (e) => {
          const files = Array.from(e.target.files || []);
          if (!files.length) return;
          setBusy(true);
          try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const { data } = await api.post(`/fm/import?fm_id=${fmId}`, fd,
              { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("David read your documents — review the import below");
            const { data: m } = await api.get(`/fm/models/${fmId}`);
            onDone(m);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed — try a .xlsx or .pdf export");
          }
          setBusy(false);
        }} />
      <span className="text-[12px] font-bold" style={{ color: "#FCD9A0" }}>
        {busy ? "Reading your documents — about 30 seconds…" : `⤴ ${label}`}
      </span>
    </label>
  );
}

function MixedStreams({ rev, setRev }) {
  const streams = rev.streams || [];
  const upd = (i, k, v) => {
    const next = streams.map((s, j) => (j === i ? { ...s, [k]: v } : s));
    setRev("streams", next);
  };
  return (
    <div>
      <Lbl hint="Each stream on its own drivers — consolidated automatically.">Revenue streams</Lbl>
      {streams.map((s, i) => (
        <div key={i} className="grid grid-cols-4 gap-2 mb-2 items-center">
          <input className={inputCls} style={inputStyle} placeholder="Stream name" value={s.name || ""} onChange={(e) => upd(i, "name", e.target.value)} />
          <NumIn value={s.volume?.value} onChange={(v) => upd(i, "volume", { shape: "flat", value: v })} placeholder="Vol/mo" />
          <NumIn value={s.price} onChange={(v) => upd(i, "price", v)} placeholder="Price" />
          <button onClick={() => setRev("streams", streams.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400"><Trash size={15} /></button>
        </div>
      ))}
      <button onClick={() => setRev("streams", [...streams, { name: "", volume: { shape: "flat", value: null }, price: null }])}
        className="text-xs font-bold inline-flex items-center gap-1" style={{ color: C.tealSoft }} data-testid="fm-add-stream">
        <Plus size={12} weight="bold" /> Add stream
      </button>
    </div>
  );
}

function SkuTable({ rev, setRev }) {
  const skus = rev.skus || [];
  const upd = (i, k, v) => setRev("skus", skus.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div data-testid="fm-skus">
      <Lbl hint="Each SKU: units/mo, price, unit cost, optional % MoM growth. COGS is computed from unit costs.">SKUs</Lbl>
      {skus.map((s, i) => (
        <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-center">
          <input className={inputCls} style={inputStyle} placeholder="SKU name" value={s.name || ""} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`fm-sku-${i}-name`} />
          <NumIn testid={`fm-sku-${i}-units`} value={s.units} onChange={(v) => upd(i, "units", v)} placeholder="Units/mo" />
          <NumIn testid={`fm-sku-${i}-price`} value={s.price} onChange={(v) => upd(i, "price", v)} placeholder="Price" />
          <NumIn testid={`fm-sku-${i}-unit_cost`} value={s.unit_cost} onChange={(v) => upd(i, "unit_cost", v)} placeholder="Unit cost" />
          <div className="flex items-center gap-1">
            <PctIn value={s.growth_mom} onChange={(v) => upd(i, "growth_mom", v)} placeholder="% MoM" />
            <button onClick={() => setRev("skus", skus.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400"><Trash size={14} /></button>
          </div>
        </div>
      ))}
      <button onClick={() => setRev("skus", [...skus, { name: "", units: null, price: null, unit_cost: null, growth_mom: null }])}
        className="text-xs font-bold inline-flex items-center gap-1" style={{ color: C.tealSoft }} data-testid="fm-add-sku">
        <Plus size={12} weight="bold" /> Add SKU
      </button>
    </div>
  );
}

const MONTH_LABELS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
function Seasonality({ rev, setRev }) {
  const seas = rev.seasonality || Array(12).fill(1);
  const upd = (i, v) => setRev("seasonality", seas.map((x, j) => (j === i ? v : x)));
  return (
    <div data-testid="fm-seasonality">
      <Lbl hint="Weight per calendar month — 1 = average, 2 = double (harvest), 0 = nothing. Applied to production volume.">Seasonality — harvest calendar</Lbl>
      <div className="grid grid-cols-6 sm:grid-cols-12 gap-1">
        {seas.map((v, i) => (
          <div key={i}>
            <div className="text-[9px] text-white/35 text-center font-bold">{MONTH_LABELS[i]}</div>
            <input type="number" step="0.1" className={inputCls + " text-center px-1"} style={inputStyle}
              value={v ?? ""} data-testid={`fm-seas-${i}`}
              onChange={(e) => upd(i, e.target.value === "" ? 1 : parseFloat(e.target.value))} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Headcount({ costs, setCost, yl }) {
  const rows = costs.headcount || [];
  const upd = (i, k, v) => setCost("headcount", rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div>
      <Lbl hint="FTE count per forecast year × gross monthly salary (loaded factor applied).">Headcount plan</Lbl>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-8 gap-1.5 mb-1.5 items-center">
          <input className={inputCls + " col-span-2"} style={inputStyle} placeholder="Role" value={r.role || ""} onChange={(e) => upd(i, "role", e.target.value)} />
          {YEARS.map((y, yi) => (
            <input key={y} type="number" className={inputCls} style={inputStyle} placeholder={yl?.[yi] || y.toUpperCase()}
              value={r.count?.[y] ?? ""} onChange={(e) => upd(i, "count", { ...r.count, [y]: e.target.value === "" ? null : parseFloat(e.target.value) })} />
          ))}
          <div className="flex items-center gap-1">
            <input type="number" className={inputCls} style={inputStyle} placeholder="Salary" value={r.salary_monthly ?? ""} onChange={(e) => upd(i, "salary_monthly", parseFloat(e.target.value) || null)} />
            <button onClick={() => setCost("headcount", rows.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400"><Trash size={14} /></button>
          </div>
        </div>
      ))}
      <button onClick={() => setCost("headcount", [...rows, { role: "", count: {}, salary_monthly: null }])}
        className="text-xs font-bold inline-flex items-center gap-1" style={{ color: C.tealSoft }} data-testid="fm-add-role">
        <Plus size={12} weight="bold" /> Add role
      </button>
    </div>
  );
}

function ItemsTable({ label, items, cols, onChange, testid, info }) {
  const upd = (i, k, v) => onChange(items.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div data-testid={testid}>
      <Lbl info={info}>{label}</Lbl>
      {items.map((r, i) => (
        <div key={i} className="flex gap-1.5 mb-1.5 items-center">
          {cols.map(([k, ph, t]) => (
            t === "pct"
              ? <input key={k} type="number" step="0.5" className={inputCls} style={inputStyle} placeholder={ph}
                  value={r[k] == null ? "" : Math.round(r[k] * 10000) / 100}
                  onChange={(e) => upd(i, k, e.target.value === "" ? null : parseFloat(e.target.value) / 100)} />
              : <input key={k} type={t === "num" ? "number" : "text"} className={inputCls} style={inputStyle} placeholder={ph}
                  value={r[k] ?? ""} onChange={(e) => upd(i, k, t === "num" ? (parseFloat(e.target.value) || null) : e.target.value)} />
          ))}
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400"><Trash size={14} /></button>
        </div>
      ))}
      <button onClick={() => onChange([...items, {}])} className="text-xs font-bold inline-flex items-center gap-1" style={{ color: C.tealSoft }} data-testid={`${testid}-add`}>
        <Plus size={12} weight="bold" /> Add
      </button>
    </div>
  );
}

function Milestones({ model, save }) {
  const rows = model.milestones || [];
  const upd = (i, k, v) => save({ milestones: rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  return (
    <div className="rounded-xl p-5 max-w-3xl" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }} data-testid="fm-milestones">
      <div className="text-sm font-extrabold mb-1" style={{ color: C.tealSoft }}>Section C-PR · Milestones — the road to first revenue</div>
      <p className="text-xs text-white/45 mb-4">What has to happen, when, for revenue to start — and whether the cash lasts until it does. Tick "ignites revenue" on the milestone that starts your revenue drivers.</p>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
          <input className={inputCls + " col-span-4"} style={inputStyle} placeholder="Milestone (e.g. First paying customer)" value={r.name || ""} onChange={(e) => upd(i, "name", e.target.value)} />
          <input type="number" className={inputCls + " col-span-2"} style={inputStyle} placeholder="Month #" value={r.month ?? ""} onChange={(e) => upd(i, "month", parseInt(e.target.value) || null)} />
          <input className={inputCls + " col-span-4"} style={inputStyle} placeholder="What it unlocks" value={r.unlocks || ""} onChange={(e) => upd(i, "unlocks", e.target.value)} />
          <label className="col-span-1 flex items-center gap-1 text-[10px] text-white/60">
            <input type="checkbox" checked={!!r.ignites_revenue} onChange={(e) => upd(i, "ignites_revenue", e.target.checked)} data-testid={`fm-milestone-ignite-${i}`} /> ignites
          </label>
          <button onClick={() => save({ milestones: rows.filter((_, j) => j !== i) })} className="text-white/40 hover:text-red-400 col-span-1"><Trash size={14} /></button>
        </div>
      ))}
      <button onClick={() => save({ milestones: [...rows, { name: "", month: null, unlocks: "", ignites_revenue: false }] })}
        className="text-xs font-bold inline-flex items-center gap-1 mt-1" style={{ color: C.tealSoft }} data-testid="fm-add-milestone">
        <Plus size={12} weight="bold" /> Add milestone
      </button>
    </div>
  );
}

function BalanceSheet({ bs, yl }) {
  const rows = [
    ["cash", "Cash"], ["receivables", "Receivables (AR)"], ["inventory", "Inventory"],
    ["fixed_assets", "Fixed assets (net)"], ["total_assets", "TOTAL ASSETS"],
    ["payables", "Payables (AP)"], ["debt", "Debt outstanding"], ["total_liabilities", "TOTAL LIABILITIES"],
    ["share_capital", "Share capital"], ["retained_earnings", "Retained earnings"], ["total_equity", "TOTAL EQUITY"],
  ];
  return (
    <div data-testid="fm-balance-sheet" className="max-w-4xl">
      <div className="text-xs text-white/45 mb-3">Section G · Annual balance sheet — hard validation: Assets = Liabilities + Equity. A model that doesn't balance cannot be submitted.</div>
      <div className="rounded-lg overflow-x-auto" style={{ border: "1px solid rgba(123,211,206,0.12)" }}>
        <table className="text-xs w-full">
          <thead><tr style={{ backgroundColor: C.panel }}>
            <th className="px-3 py-2 text-left text-white/50 font-bold">Year end</th>
            {YEARS.map((y, yi) => <th key={y} className="px-3 py-2 text-right font-bold" style={{ color: C.orange }}>{yl?.[yi] || y.toUpperCase()}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(([k, label]) => (
              <tr key={k} className="border-t border-white/5">
                <td className={`px-3 py-1.5 whitespace-nowrap ${label.startsWith("TOTAL") ? "font-bold text-white" : "text-white/60"}`}>{label}</td>
                {YEARS.map((y) => (
                  <td key={y} className={`px-3 py-1.5 text-right whitespace-nowrap ${label.startsWith("TOTAL") ? "font-bold" : "text-white/80"}`}
                    style={label.startsWith("TOTAL") ? { color: C.tealSoft } : undefined}>
                    {fmt(bs.years[y]?.[k] ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-white/10">
              <td className="px-3 py-2 font-bold text-white">Balance check</td>
              {YEARS.map((y) => {
                const v = bs.years[y];
                return (
                  <td key={y} className="px-3 py-2 text-right" data-testid={`fm-bs-check-${y}`}>
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full"
                      style={{ backgroundColor: v?.balanced ? "rgba(44,197,168,0.15)" : "rgba(248,113,113,0.15)", color: v?.balanced ? "#2CC5A8" : "#F87171" }}>
                      {v?.balanced ? "BALANCED ✓" : `OFF BY ${fmt(v?.check_diff ?? 0)}`}
                    </span>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Historicals({ model, save, fmId, cal }) {
  const [pulling, setPulling] = useState(false);
  const hist = model.historicals || {};
  const hl = cal?.historical_labels || {};
  const slots = [
    ["h1", hl.h1 ? `H-1 · ${hl.h1}` : "Last year (H-1)"],
    ["h2", hl.h2 ? `H-2 · ${hl.h2}` : "Two years ago (H-2)"],
    ["h3", hl.h3 ? `H-3 · ${hl.h3}` : "Three years ago (H-3)"],
  ];
  const fields = [["revenue", "Revenue"], ["cogs", "COGS"], ["opex", "Operating costs"], ["net_profit", "Net profit"], ["closing_cash", "Closing cash"]];
  const upd = (slot, k, v) => save({ historicals: { [slot]: { ...(hist[slot] || {}), [k]: v } } });
  const pull = async () => {
    setPulling(true);
    try {
      const { data } = await api.post(`/fm/models/${fmId}/historicals/pull-mgmt`);
      toast.success(`Pulled ${data.years_pulled.length} year(s) from your Management Accounts`);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pull failed");
    }
    setPulling(false);
  };
  return (
    <div className="max-w-4xl" data-testid="fm-historicals">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-white/45">Section C · Historical actuals — funders read history before forecasts. Enter annual totals, or pull them straight from your Management Accounts.</div>
        <button onClick={pull} disabled={pulling} data-testid="fm-pull-mgmt-btn"
          className="text-xs font-bold px-3 py-2 rounded-md whitespace-nowrap"
          style={{ backgroundColor: C.teal, color: "#fff" }}>
          {pulling ? "Pulling…" : "Pull from Management Accounts"}
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {slots.map(([slot, label]) => (
          <div key={slot} className="rounded-xl p-4 space-y-3" style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.14)" }}>
            <div className="text-sm font-extrabold" style={{ color: C.tealSoft }}>{label}
              {hist[slot]?.source === "mgmt_accounts" && <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(245,158,11,0.15)", color: C.orange }}>FROM MGMT ACCOUNTS</span>}
            </div>
            <input className={inputCls} style={inputStyle} placeholder="Year label e.g. 2025" value={hist[slot]?.year_label || ""} onChange={(e) => upd(slot, "year_label", e.target.value)} data-testid={`fm-hist-${slot}-year`} />
            {fields.map(([k, l]) => (
              <div key={k}>
                <Lbl>{l}</Lbl>
                <NumIn testid={`fm-hist-${slot}-${k}`} value={hist[slot]?.[k]} onChange={(v) => upd(slot, k, v)} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthlyTable({ data, fields, title, testid, cal }) {
  const keys = fields || Object.keys(data[0] || {});
  return (
    <div data-testid={testid}>
      <div className="text-xs text-white/45 mb-3">{title}</div>
      {YEARS.map((y, yi) => (
        <div key={y} className="mb-6 overflow-x-auto rounded-lg" style={{ border: "1px solid rgba(123,211,206,0.12)" }}>
          <table className="text-[11px] w-full">
            <thead>
              <tr style={{ backgroundColor: C.panel }}>
                <th className="px-3 py-2 text-left font-bold whitespace-nowrap" style={{ color: C.orange }}>
                  {cal?.year_labels?.[yi] || y.toUpperCase()}
                  {cal?.year_bands?.[yi] && <div className="text-[9px] font-semibold text-white/35">{cal.year_bands[yi]}</div>}
                </th>
                {Array.from({ length: 12 }, (_, m) => (
                  <th key={m} className="px-2 py-2 text-right text-white/40 font-semibold whitespace-nowrap">
                    {cal?.month_labels?.[yi * 12 + m] || `M${yi * 12 + m + 1}`}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-bold" style={{ color: C.tealSoft }}>Year</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const rows = data.slice(yi * 12, yi * 12 + 12);
                const total = rows.reduce((s, r) => s + (parseFloat(r[k]) || 0), 0);
                return (
                  <tr key={k} className="border-t border-white/5">
                    <td className="px-3 py-1.5 text-white/60 whitespace-nowrap capitalize">{k.replaceAll("_", " ")}</td>
                    {rows.map((r, m) => (
                      <td key={m} className="px-2 py-1.5 text-right text-white/80 whitespace-nowrap">{fmt(parseFloat(r[k]) || 0)}</td>
                    ))}
                    <td className="px-3 py-1.5 text-right font-bold whitespace-nowrap" style={{ color: C.tealSoft }}>
                      {["active", "users", "gross_margin", "cumulative_net"].includes(k) ? "" : fmt(total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

